import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ClockIcon, TruckIcon, PhoneIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import db from '../services/database';
const OrderConfirmation = () => {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [order, setOrder] = useState(null);
  useEffect(() => {
    const saveOrderToDatabase = async () => {
      // Get current order ID from localStorage
      const currentOrderId = localStorage.getItem('currentOrder');
      if (!currentOrderId) {
        navigate('/customer');
        return;
      }
      // Get orders from localStorage
      const orders = JSON.parse(localStorage.getItem('orders') || '[]');
      const currentOrder = orders.find(order => order.id === currentOrderId);
      if (currentOrder) {
        setOrder(currentOrder);
        // Save order to database
        try {
          // Check if order already exists in database
          const existingOrder = await db.orders.get(currentOrder.id);
          if (!existingOrder) {
            // Save to orders table
            await db.orders.add({
              OrderID: currentOrder.id,
              User_ID: user?.User_ID || 1,
              Order_Date: currentOrder.date,
              Total_Amount: currentOrder.total,
              Order_Status: 'Pending'
            });
            // Save order details with item name and price
            for (const item of currentOrder.items) {
              // First, ensure the menu item exists in the database
              const existingMenuItem = await db.menuItems.get(item.id);
              if (!existingMenuItem) {
                // If menu item doesn't exist, add it to the database
                await db.menuItems.add({
                  Item_ID: item.id,
                  Item_Name: item.name,
                  Description: item.description || '',
                  Price: item.price,
                  Category: item.category || 'Other',
                  Image_URL: item.image || '',
                  Availability_Status: 'Available'
                });
              }
              // Now save the order detail
              await db.orderDetails.add({
                Order_ID: currentOrder.id,
                Item_ID: item.id,
                Quantity: item.quantity,
                Subtotal: item.price * item.quantity
              });
            }
            console.log('Order saved to database successfully');
          }
        } catch (error) {
          console.error('Error saving order to database:', error);
        }
      } else {
        navigate('/customer');
      }
      // Clear the current order from localStorage
      localStorage.removeItem('currentOrder');
      // Cleanup checkout details
      localStorage.removeItem('checkoutDetails');
    };
    saveOrderToDatabase();
  }, [navigate, user]);
  if (!order) {
    return <div data-id="element-1465">Loading...</div>;
  }
  return <div className="bg-amber-50 min-h-screen py-8" data-id="element-1466">
      <div className="max-w-3xl mx-auto px-4" data-id="element-1467">
        <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-1468">
          <div className="p-6 bg-green-50 border-b border-green-100 flex items-center" data-id="element-1469">
            <CheckCircleIcon className="h-12 w-12 text-green-600 mr-4" data-id="element-1470" />
            <div data-id="element-1471">
              <h1 className="text-2xl font-bold text-green-800" data-id="element-1472">
                Order Confirmed!
              </h1>
              <p className="text-green-700" data-id="element-1473">
                Your order has been successfully placed.
              </p>
            </div>
          </div>
          <div className="p-6" data-id="element-1474">
            <div className="mb-6" data-id="element-1475">
              <div className="flex justify-between items-center mb-2" data-id="element-1476">
                <h2 className="text-xl font-semibold text-amber-900" data-id="element-1477">
                  Order #{order.id}
                </h2>
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium" data-id="element-1478">
                  {order.status}
                </span>
              </div>
              <p className="text-sm text-gray-600" data-id="element-1479">
                Placed on {new Date(order.date).toLocaleString()}
              </p>
            </div>
            <div className="mb-6" data-id="element-1480">
              <h3 className="text-lg font-medium text-amber-800 mb-3" data-id="element-1481">
                Estimated Delivery
              </h3>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-1482">
                <div className="flex items-center" data-id="element-1483">
                  <ClockIcon className="h-5 w-5 text-amber-800 mr-2" data-id="element-1484" />
                  <span className="font-medium" data-id="element-1485">
                    Estimated delivery time: 30-45 minutes
                  </span>
                </div>
                <div className="mt-3 flex items-start" data-id="element-1486">
                  <TruckIcon className="h-5 w-5 text-amber-800 mr-2 mt-0.5" data-id="element-1487" />
                  <div data-id="element-1488">
                    <span className="font-medium block" data-id="element-1489">Delivery Address:</span>
                    <span className="text-sm text-gray-700" data-id="element-1490">
                      {order.deliveryAddress}
                    </span>
                  </div>
                </div>
                <div className="mt-3 flex items-center" data-id="element-1491">
                  <PhoneIcon className="h-5 w-5 text-amber-800 mr-2" data-id="element-1492" />
                  <div data-id="element-1493">
                    <span className="font-medium" data-id="element-1494">Contact Number:</span>
                    <span className="text-sm text-gray-700 ml-1" data-id="element-1495">
                      {order.contactNumber}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6" data-id="element-1496">
              <h3 className="text-lg font-medium text-amber-800 mb-3" data-id="element-1497">
                Order Details
              </h3>
              <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-1498">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1499">
                  <thead className="bg-gray-50" data-id="element-1500">
                    <tr data-id="element-1501">
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1502">
                        Item
                      </th>
                      <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1503">
                        Qty
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1504">
                        Price
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1505">
                    {order.items.map((item, index) => <tr key={index} data-id="element-1506">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1507">
                          {item.name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-1508">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-1509">
                          ₱{(item.price * item.quantity).toFixed(2)}
                        </td>
                      </tr>)}
                  </tbody>
                  <tfoot className="bg-gray-50" data-id="element-1510">
                    <tr data-id="element-1511">
                      <td colSpan={2} className="px-4 py-2 text-right text-sm font-medium text-gray-500" data-id="element-1512">
                        Subtotal
                      </td>
                      <td className="px-4 py-2 text-right text-sm font-medium text-gray-900" data-id="element-1513">
                        ₱{order.subtotal.toFixed(2)}
                      </td>
                    </tr>
                    <tr data-id="element-1514">
                      <td colSpan={2} className="px-4 py-2 text-right text-sm font-medium text-gray-500" data-id="element-1515">
                        Delivery Fee
                      </td>
                      <td className="px-4 py-2 text-right text-sm font-medium text-gray-900" data-id="element-1516">
                        ₱{order.deliveryFee.toFixed(2)}
                      </td>
                    </tr>
                    <tr data-id="element-1517">
                      <td colSpan={2} className="px-4 py-2 text-right text-sm font-bold text-gray-900" data-id="element-1518">
                        Total
                      </td>
                      <td className="px-4 py-2 text-right text-sm font-bold text-gray-900" data-id="element-1519">
                        ₱{order.total.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            <div className="mb-6" data-id="element-1520">
              <h3 className="text-lg font-medium text-amber-800 mb-3" data-id="element-1521">
                Payment Information
              </h3>
              <p className="text-gray-700" data-id="element-1522">
                <span className="font-medium" data-id="element-1523">Method:</span>{' '}
                {order.paymentMethod}
              </p>
            </div>
            <div className="border-t border-gray-200 pt-6 flex flex-col sm:flex-row justify-between items-center" data-id="element-1524">
              <p className="text-gray-700 mb-4 sm:mb-0" data-id="element-1525">
                Thank you for your order! You can track the status of your order
                in your account.
              </p>
              <div className="flex space-x-3" data-id="element-1526">
                <Link to={`/order/${order.id}`} className="px-4 py-2 border border-amber-800 text-amber-800 rounded-md hover:bg-amber-50" data-id="element-1527">
                  Track Order
                </Link>
                <Link to="/customer" className="px-4 py-2 bg-amber-800 text-white rounded-md hover:bg-amber-900" data-id="element-1528">
                  My Orders
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default OrderConfirmation;